import baseStyled, { css as baseCss, ThemedCssFunction, ThemedStyledInterface } from 'styled-components';
import { AssociatedPersonnelTheme } from './themes/types';

const styled = baseStyled as ThemedStyledInterface<AssociatedPersonnelTheme>;
const css = baseCss as ThemedCssFunction<AssociatedPersonnelTheme>;

export const AssociatedPersonnel = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100%;
  background-color: #f8fafc;
  box-sizing: border-box;
`;

// Centered content column - keeps the page from stretching edge-to-edge on wide screens.
export const PageContent = styled.div`
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px 24px 32px 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const PanelsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 260px;
  gap: 20px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const PanelHeader = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.mode?.associatedPersonnel?.panelHeaderBGColor || '#267dac'};
    color: ${theme.mode?.associatedPersonnel?.panelHeaderFontColor || '#ffffff'};
  `}
  padding: 10px 16px;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.02em;
`;

export const ActionsPanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

// Kept for backwards compat with any leftover references; page layout now uses PageContent/PanelsRow.
export const ContentWrapper = styled.div`
  display: flex;
  gap: 20px;
`;

export const PersonnelListContainer = styled.div``;

export const PersonnelCategoryTitle = styled.h2`
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  margin: 0;
`;

export const PersonnelList = styled.div`
  padding: 10px;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 38vh;
  min-height: 80px;
  background: #f8fafc;

  &:empty::after {
    content: '—';
    display: block;
    text-align: center;
    color: #94a3b8;
    padding: 12px 0;
  }
`;

export const ButtonContainer = styled.div`
  margin: 10px;
  padding: 10px;
  border: 1px solid lightgrey;
  border-radius: 5px;
`;

export const ModalContainer = styled.div`
  ${({ theme }) => css`
    background: ${theme.mode?.associatedPersonnel?.modalContainerBGColor || '#ffffff'};
  `}
  border: 2px solid #267dac;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

export const ModalHeaderContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.mode?.associatedPersonnel?.modalHeaderContainerBGColor || '#267dac'};
    color: ${theme.mode?.associatedPersonnel?.modalHeaderContainerFontColor || '#ffffff'};
  `}
  border-radius: 6px;
  padding: 8px 10px;
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 12px;
`;

export const FormGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns || 2}, 1fr);
  gap: 16px;
  margin-bottom: 14px;
  width: 100%;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .plasma-input-wrapper,
  input,
  .ant-select {
    width: 100% !important;
  }
`;

export const InlineFormGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Label = styled.label`
  font-weight: bold;
  font-size: 13px;
  color: #334155;
`;

export const AssociatedPersonnelListContainer = styled.div``;

export const AssociatedPersonnelItem = styled.div<{
  isMouseEntered: boolean;
  isClicked: boolean;
  flaggedPerson: boolean;
}>`
  color: #1e293b;
  margin-bottom: 6px;
  padding: 2px 4px;
  cursor: ${(props) => (props.isMouseEntered ? 'pointer' : 'default')};
  border-radius: 6px;
  border-left: 4px solid ${(props) => (props.flaggedPerson ? '#0d9488' : 'transparent')};
  background-color: ${(props) => (props.isClicked ? '#dbeafe' : '#ffffff')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: background-color 120ms ease;

  &:hover {
    background-color: ${(props) => (props.isClicked ? '#dbeafe' : '#eef4fa')};
  }
`;

export const PersonnelItem = styled.div<{ isMouseEntered: boolean; isClicked: boolean }>`
  color: #1e293b;
  cursor: ${(props) => (props.isMouseEntered ? 'pointer' : 'default')};
  margin-bottom: 6px;
  padding: 2px 4px;
  background-color: ${(props) => (props.isClicked ? '#dbeafe' : '#ffffff')};
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: background-color 120ms ease;

  &:hover {
    background-color: ${(props) => (props.isClicked ? '#dbeafe' : '#eef4fa')};
  }
`;

export const PersonnelItemElementBold = styled.div`
  font-weight: 600;
  padding: 4px;
  color: #0f172a;
`;

export const PersonalItemElement = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
  color: #475569;
  font-size: 13px;
`;

export const PersonnelRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 6px 8px;
`;

export const CompanyContextBar = styled.div`
  padding: 6px 12px;
  border-radius: 6px;
  background: #eef4fa;
  color: #334155;
  font-size: 13px;
`;

const PILL_COLORS: Record<string, { bg: string; fg: string }> = {
  external: { bg: '#e0e7ff', fg: '#3730a3' },
  region: { bg: '#dcfce7', fg: '#166534' },
  coc: { bg: '#fef3c7', fg: '#92400e' },
  low: { bg: '#e5e7eb', fg: '#374151' },
  medium: { bg: '#fde68a', fg: '#92400e' },
  high: { bg: '#fecaca', fg: '#991b1b' },
};

export const Pill = styled.span<{ $tone: keyof typeof PILL_COLORS }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  margin-left: 8px;
  white-space: nowrap;
  background: ${(props) => PILL_COLORS[props.$tone]?.bg ?? '#e5e7eb'};
  color: ${(props) => PILL_COLORS[props.$tone]?.fg ?? '#374151'};
`;

export const DropZone = styled.div<{ $isOver: boolean }>`
  border-radius: 6px;
  transition: background-color 120ms ease, box-shadow 120ms ease;
  background-color: ${(props) => (props.$isOver ? 'rgba(38, 125, 172, 0.1)' : 'transparent')};
  box-shadow: ${(props) => (props.$isOver ? 'inset 0 0 0 2px #267DAC' : 'none')};
`;

export const RemoveDropZone = styled.div<{ $isOver: boolean }>`
  padding: 14px 10px;
  text-align: center;
  font-size: 12px;
  line-height: 1.4;
  border-radius: 6px;
  border: 2px dashed ${(props) => (props.$isOver ? '#dc2626' : '#cbd5e1')};
  color: ${(props) => (props.$isOver ? '#dc2626' : '#94a3b8')};
  background-color: ${(props) => (props.$isOver ? 'rgba(220, 38, 38, 0.06)' : 'transparent')};
  transition: all 120ms ease;
`;

export const DraggableWrapper = styled.div<{ $isDragging: boolean }>`
  cursor: grab;
  opacity: ${(props) => (props.$isDragging ? 0.4 : 1)};
`;
